import { useEffect, useState } from 'react';
import requiredFieldConfigRaw from '../../data/requiredFieldConfig';
import { fieldReader, fieldWriter } from '../../services/loanApi';
import { getAccessToken } from '../../services/authApi';
import { getZipLocations } from '../../services/locationServiceApi';
import US_STATES from '../../data/usStates';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';
import { menuItemsCP, menuItemsBP } from '../menu/menuItems';
import { collectConditionFields, evaluateCompositeCondition, isInvalidNumber, isFieldEffectivelyEmpty } from '../../utils/conditionUtils';
import { runLicensingGateForPage } from '../../utils/licenseValidation';
import { resolveFieldOptions } from '../../utils/loanProgramUtil';

const PricingValidationRenderer = ({ pageName, config, redirectPage }) => {
    const [token, setToken] = useState(null);
    const [loanId, setLoanId] = useState('');
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [showZipPopup, setShowZipPopup] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [zipSelection, setZipSelection] = useState({ cityId: '', stateId: '' });

    const [pageConfig, setPageConfig] = useState(null);
    const [skipModuleValidations, setSkipModuleValidations] = useState(false);

    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");
    const [licenseStatus, setLicenseStatus] = useState({
        blocked: false,
        companyHas: false,
        loHas: false,
        stateCode: '',
        city: '',
        stateName: '',
        reason: '',
        message: '',
        shortMessage: '',
        brokerSelection: ''
    });


    const init = async () => {
        try {
            window.elli?.script?.guest?.create?.();
            const loanObject = await window.elli.script.getObject("loan");
            const loan = await loanObject.all();
            setLoanId(loan.id);

            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const accessToken = await getAccessToken(authCode);
            setToken(accessToken);

            const fieldIds = [
                ...new Set([
                    "CX.LOAN.TYPE",
                    "3238",
                    'CX.LEGAL.ENTITY.TYPE',
                    "CX.TPOC.OVERRIDE.VALIDATIONS",
                    "1401",
                    "CX.EXC.PRE.ISAPPROVED",
                    'LCP.X1',
                    'LCP.X8',
                    "4460",
                    "300#2",
                    "300#3",
                    "300#4",
                    "300#5",
                    ...config.FieldGroups.flatMap(group =>
                        (group.Fields || [])
                            .filter(f => f.id)
                            .map(f => f.id)
                    ),
                    ...config.FieldGroups.flatMap(group => collectConditionFields(group.NotRequiredIf)),
                    ...config.FieldGroups.flatMap(group => collectConditionFields(group.ShowIf)),
                ])
            ];

            const values = await fieldReader(accessToken, fieldIds, loan.id);

            const overrideValidations =
                (values["CX.TPOC.OVERRIDE.VALIDATIONS"] || "")
                    .toString()
                    .trim()
                    .toUpperCase();

            const skipModules = overrideValidations === "Y";
            setSkipModuleValidations(skipModules);

            const loanType = values["CX.LOAN.TYPE"];
            const stateCode = (values["14"] || "").trim();
            const city = (values["12"] || "").trim();
            const tpoOrgId = (values["TPO.X15"] || "").trim();
            const tpoUserId = (values["TPO.X62"] || "").trim();
            const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

            // filter by loan type like before
            let filteredGroups = config.FieldGroups.filter(
                g => !g.LoanType || g.LoanType === loanType
            );

            if (skipModules) {
                filteredGroups = filteredGroups.filter(
                    g => (g.Type || "").toLowerCase() !== "module"
                );
            }

            // run licensing gate
            let status = {
                blocked: false,
                companyHas: false,
                loHas: false,
                stateCode: '',
                city: '',
                stateName: '',
                reason: '',
                message: '',
                shortMessage: '',
                brokerSelection: ''
            };

            if (!skipModules) {
                status = await runLicensingGateForPage(
                    loanType,
                    stateCode,
                    tpoOrgId,
                    tpoUserId,
                    city,
                    legalEntityType
                );
            }

            setLicenseStatus({ ...status, city });

            // if blocked, move Licensing to top (optional, but matches your Disclosure page)
            if (!skipModules && status.blocked) {
                filteredGroups = [
                    ...filteredGroups.filter(g => g.Group === "Licensing"),
                    ...filteredGroups.filter(g => g.Group !== "Licensing"),
                ];
            }

            setPageConfig({ ...config, FieldGroups: filteredGroups });
            setFieldValues(values);
        } catch (error) {
            console.error(`Error initializing ${pageName} page:`, error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, []);


    const shouldShowGroup = (group) => {
        if (skipModuleValidations && group.Type === "Module") {
            return false;
        }

        // Order Credit: show when any borrower pair credit is missing
        if (group.Group === 'Order Credit') {
            if (isGroupNotRequired(group, fieldValues)) return false;
            return !isOrderCreditCleared(fieldValues);
        }
        //console.log(group.Group);
        // Special: Mortgage Fees should show ONLY when Borrower Paid AND 389 is empty
        if (group.Group === 'Mortgage Fees') {
            const isBorrowerPaid =
                (fieldValues['LCP.X1'] || '').trim().toLowerCase() === 'borrower paid';
            if (!isBorrowerPaid) return false;

            const isBusinessLoan =
                (fieldValues['CX.LOAN.TYPE'] || '').toString().trim().toLowerCase() === 'business';

            return isInvalidNumber(fieldValues['389'], isBusinessLoan);
        }

        // Show Licensing group only when blocked
        if (group.Group === "Licensing") {
            return !skipModuleValidations && licenseStatus.blocked;
        }

        // LO Comp Not Configured (Consumer + Lender Paid + LCP.X8 empty) => block Send/Request Disclosures
        if (group.Group === 'LO Comp Not Configured') {
            if (skipModuleValidations) return false;

            const loanType = (fieldValues['CX.LOAN.TYPE'] || '').toString().trim().toLowerCase();
            const loCompType = (fieldValues['LCP.X1'] || '').toString().trim();
            const loCompBasePlanTotal = (fieldValues['LCP.X8'] || '').toString().trim();

            const isConsumer = loanType === 'consumer';
            const isLenderPaid =
                loCompType.localeCompare('Lender Paid', undefined, { sensitivity: 'accent' }) === 0;

            return isConsumer && isLenderPaid && isFieldEffectivelyEmpty('LCP.X8', loCompBasePlanTotal);
        }

        // Pricing Exception Approved
        if (group.Group === 'Pricing Exception Approved') {
            if (skipModuleValidations) return false;

            const programSelected = !isEmptyField(fieldValues['1401']);
            const exceptionApproved =
                (fieldValues['CX.EXC.PRE.ISAPPROVED'] || '')
                    .toString()
                    .trim()
                    .toUpperCase() === 'Y';
            return programSelected &&
                exceptionApproved;
        }


        // Show both fields when either doc or income is empty, or when the current income isn't valid for the selected doc type.
        if (group.Group === 'Documentation vs Income') {
            // honor NotRequiredIf
            if (isGroupNotRequired(group, fieldValues)) return false;

            const docVal = (fieldValues['CX.DOC.TYPE'] ?? '').toString().trim();
            const incVal = (fieldValues['CX.INCOME.TYPE'] ?? '').toString().trim();

            // If either is empty -> show group (both fields will be rendered)
            if (isEmptyField(docVal) || isEmptyField(incVal)) return true;

            // find mapping from JSON (dependentOptions is on CX.DOC.TYPE field)
            const docField = (group.Fields || []).find(f => f.id === 'CX.DOC.TYPE');
            const mapping = docField?.dependentOptions?.map ?? {};

            const allowed = Array.isArray(mapping[docVal]) ? mapping[docVal].map(v => (v ?? '').toString().trim()) : [];

            // if doc has mapping and income value not in allowed -> show group so user can correct
            if (allowed.length > 0 && !allowed.some(a => a.localeCompare(incVal, undefined, { sensitivity: 'accent' }) === 0)) {
                return true;
            }

            // otherwise treat as cleared / not shown
            return false;
        }


        if (group.Type === "Module") {
            return !isModuleGroupCleared(group); // show if module has missing fields
        }

        if (group.Type === "Field") {
            return group.Fields.some(field => {
                if (field.hidden) return false;
                const value = fieldValues[field.id];
                return isEmptyField(value);
            });
        }

        return false;
    };


    const handleSave = async () => {
        const updated = [];
        const missingFields = [];

        pageConfig?.FieldGroups
            .filter(group => group.Type === 'Field' && shouldShowGroup(group))
            .forEach(group => {
                group.Fields.forEach(field => {
                    if (field.hidden) return;

                    const el = document.getElementById(`field-${field.id}`);
                    if (!el) return;

                    const uiValue = el.value?.trim?.() ?? '';
                    const originalValue = String(fieldValues[field.id] ?? '').trim();

                    const isVisible = !field.hidden;
                    const isChanged = uiValue !== originalValue;

                    const isBusinessLoan =
                        (fieldValues['CX.LOAN.TYPE'] || '').toString().trim().toLowerCase() === 'business';

                    const isInvalid =
                        String(field.id) === "389"
                            ? isInvalidNumber(uiValue, isBusinessLoan)
                            : uiValue === '';

                    if (isVisible && isChanged && !isInvalid) {
                        updated.push({ id: field.id, value: uiValue });
                    }

                    if (isInvalid) {
                        missingFields.push(field.name);
                    }
                });
            });


        if (missingFields.length > 0) {
            showToastMsg(
                `The following required fields are missing:<br>- ${missingFields.join('<br>- ')}`
            );

            return;
        }

        // Save and redirect if nothing is missing
        setLoading(true);
        setLoadingText("Saving...");
        try {
            await fieldWriter(token, loanId, updated);

            if (!allModulesCleared()) {
                init();
                if (redirectPage === "Product Pricing & Lock") {
                    showToastMsg("Please order Credit before Pricing.");
                }
                else {
                    showToastMsg("Please run pricing before ordering Smart Fees.");
                }
            } else {
                await redirectToPage(redirectPage, loanId);
            }

        } catch (err) {
            console.log("Save failed", err);
            if (err.response?.data?.errors?.length > 0) {
                const errors = err.response.data.errors;
                const formattedErrors = errors
                    .map(e => `• ${e.details || e.summary}`)
                    .join('<br>');

                showToastMsg(`${formattedErrors}`);
            } else if (err.response?.data?.details) {
                showToastMsg(`${err.response.data.details}`);
            } else {
                showToastMsg("Save failed due to an unexpected error.");
            }
        }
        finally {
            setLoading(false);
        }
    };


    const showToastMsg = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 10000);
    };

    const handleZipBlur = async (field) => {
        if (field.datatype !== 'Zip') return;
        const zip = document.getElementById(`field-${field.id}`)?.value;
        if (!zip || zip.length < 5) return;

        const results = await getZipLocations(zip, token);
        const { city: cityId, state: stateId } = field;

        if (results.length === 1) {
            document.getElementById(`field-${cityId}`).value = results[0].city;
            document.getElementById(`field-${stateId}`).value = results[0].abbrev;
        } else if (results.length > 1) {
            setCityOptions(results);
            setZipSelection({ cityId, stateId });
            setShowZipPopup(true);
        }
    };

    const isEmptyField = (val) => {
        const trimmed = (val || '').trim();
        return trimmed === '' || trimmed === '//' || trimmed === '  /  /    ';
    };

    const getBorrowerPairCount = (values) => {
        const raw = values["4460"];
        const count = parseInt(raw, 10);
        return Number.isFinite(count) && count > 0 ? count : 1;
    };

    const getMissingOrderCreditPairFields = (values) => {
        const pairCount = getBorrowerPairCount(values);
        const missing = [];

        for (let pairIndex = 1; pairIndex <= pairCount; pairIndex++) {
            const fieldId = `300#${pairIndex}`;
            if (isEmptyField(values[fieldId])) {
                missing.push(fieldId);
            }
        }

        return missing;
    };

    const isOrderCreditCleared = (values) => {
        return getMissingOrderCreditPairFields(values).length === 0;
    };

    const isGroupNotRequired = (group, values) =>
        group.NotRequiredIf ? evaluateCompositeCondition(group.NotRequiredIf, values) : false;

    const isModuleGroupCleared = (group) => {
        if (skipModuleValidations && group.Type === "Module") {
            return true;
        }

        //console.log('isModuleGroupCleared: ' + group.Group);

        // Licensing is never cleared when blocked
        if (group.Group === "Licensing" && licenseStatus.blocked) return false;

        // Order Credit: cleared only when all borrower pair credit fields are filled
        if (group.Group === "Order Credit") {
            if (isGroupNotRequired(group, fieldValues)) return true;
            return isOrderCreditCleared(fieldValues);
        }

        // LO Comp Not Configured is condition-based (usually no fields)
        if (group.Group === "LO Comp Not Configured") {
            const loanType = (fieldValues["CX.LOAN.TYPE"] || "").toString().trim().toLowerCase();
            const loCompType = (fieldValues["LCP.X1"] || "").toString().trim();
            const loCompBasePlanTotal = (fieldValues["LCP.X8"] || "").toString().trim();

            const isConsumer = loanType === "consumer";
            const isLenderPaid =
                loCompType.localeCompare("Lender Paid", undefined, { sensitivity: "accent" }) === 0;

            const shouldBlock = isConsumer && isLenderPaid && isFieldEffectivelyEmpty('LCP.X8', loCompBasePlanTotal);

            // cleared when NOT blocking; NOT cleared when blocking
            return !shouldBlock;
        }

        // Pricing Exception Approved behaves like LO Comp
        if (group.Group === "Pricing Exception Approved") {

            const programSelected = !isEmptyField(fieldValues["1401"]);

            const exceptionApproved =
                (fieldValues["CX.EXC.PRE.ISAPPROVED"] || "")
                    .toString()
                    .trim()
                    .toUpperCase() === "Y";

            const shouldBlock =
                programSelected &&
                exceptionApproved;

            return !shouldBlock; // NOT cleared when blocking
        }

        if (group.Group === 'Milestone') {
            if (group.ShowIf) {
                return !evaluateCompositeCondition(group.ShowIf, fieldValues);
            }
        }

        // Disclosures Sent (not yet submitted) => no pricing in Polly; NOT cleared while ShowIf holds
        if (group.Group === 'Disclosures Sent') {
            return !evaluateCompositeCondition(group.ShowIf, fieldValues);
        }

        // If group is explicitly not required via NotRequiredIf -> treat as cleared
        if (isGroupNotRequired(group, fieldValues)) return true;

        // If group has ShowIf and the show condition is NOT met -> treat as cleared (not applicable)
        if (group.ShowIf && !evaluateCompositeCondition(group.ShowIf, fieldValues)) return true;

        // Only consider non-hidden fields when determining if the group is cleared
        const visibleFields = (group.Fields || []).filter(f => !f.hidden && f.id);

        // No visible fields means nothing to fill -> cleared
        if (visibleFields.length === 0) return true;

        // Documentation vs Income must validate mapping, not just non-empty
        if (group.Group === 'Documentation vs Income') {
            const docVal = String(fieldValues['CX.DOC.TYPE'] ?? '').trim();
            const incVal = String(fieldValues['CX.INCOME.TYPE'] ?? '').trim();

            // If either is empty -> not cleared
            if (isEmptyField(docVal) || isEmptyField(incVal)) return false;

            // Read mapping defensively (accept either 'map' or 'mapping')
            const docField = visibleFields.find(f => f.id === 'CX.DOC.TYPE') || (group.Fields || []).find(f => f.id === 'CX.DOC.TYPE');
            const mapping = docField?.dependentOptions?.map ?? docField?.dependentOptions?.mapping ?? {};
            const allowed = Array.isArray(mapping[docVal]) ? mapping[docVal].map(v => String(v ?? '').trim()) : [];

            // Consider group cleared only when income value is allowed for the chosen doc type
            const allowedMatch = allowed.length > 0 && allowed.some(a => a.localeCompare(incVal, undefined, { sensitivity: 'accent' }) === 0);
            return allowedMatch;
        }

        // Group is cleared only if all visible fields have non-empty values
        return visibleFields.every(field => {
            const value = fieldValues[field.id];

            const isBusinessLoan =
                (fieldValues['CX.LOAN.TYPE'] || '').toString().trim().toLowerCase() === 'business';

            if (String(field.id) === "389") {
                return !isInvalidNumber(value, isBusinessLoan);
            }

            return !isEmptyField(value);
        });
    };


    const allFieldsCleared = () => {
        let isCleared = pageConfig?.FieldGroups
            .filter(group => group.Type === 'Field')
            .every(group => isModuleGroupCleared(group));

        return isCleared;
    };

    const allModulesCleared = () => {
        if (skipModuleValidations) return true;

        return pageConfig?.FieldGroups
            .filter(group => group.Type === "Module")
            .filter(group => shouldShowGroup(group) || group.Group === "Licensing") // optional
            .every(group => isModuleGroupCleared(group));
    };

    useEffect(() => {
        let hideTimer, vanishTimer;

        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");
                vanishTimer = setTimeout(() => hideToast(), 500);
            }, 4000);
        }

        return () => {
            clearTimeout(hideTimer);
            clearTimeout(vanishTimer);
        };
    }, [showToast]);

    const hideToast = () => {
        setShowToast(false);
        setToastVisible(false);
    };

    const redirectToPage = async (page, loanId) => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");

        const loanTypeValue = fieldValues["CX.LOAN.TYPE"]?.toLowerCase() || "";
        const loanOfficerNMLSID = fieldValues["3238"]?.trim(); // NMLS Field ID

        const isBusinessLoan = loanTypeValue.includes("business");
        const menuItems = isBusinessLoan ? menuItemsBP : menuItemsCP;

        // Find menu item
        let menuItem = menuItems.find(item => item.displayName === page || item.label === page);

        // Check subsections if not found in top-level
        if (!menuItem) {
            for (const parent of menuItems) {
                if (parent.subsections) {
                    const sub = parent.subsections.find(subItem => subItem.displayName === page || subItem.label === page);
                    if (sub) {
                        menuItem = sub;
                        break;
                    }
                }
            }
        }

        // Override CorelogicCredco → DUAL AUS for Business Loan + NMLS ID
        if (isBusinessLoan && loanOfficerNMLSID && menuItem?.label === "CorelogicCredco") {
            menuItem = menuItemsCP.find(item => item.label === "DUAL AUS"); // fallback to CP for DUAL AUS config
        }

        // Redirect to external page if extPageId is present
        if (menuItem?.extPageId) {
            await tpoAppObject.pageRedirect("ExternalLoanPage", {
                id: loanId,
                extpageid: menuItem.extPageId,
                pagename: menuItem.label
            });
        } else {
            // Default internal redirection
            await tpoAppObject.pageRedirect(menuItem.label, { id: loanId });
        }
    };

    // - clears CX.INCOME.TYPE if it's not valid for the new doc type
    const handleDocTypeChange = (newDocType, group) => {
        const incomeId = (group?.Fields || []).find(f => f.id === 'CX.DOC.TYPE')?.dependentOptions?.bindTo
            ? group.Fields.find(f => f.id === 'CX.DOC.TYPE').dependentOptions.bindTo
            : 'CX.INCOME.TYPE';

        // update doc type
        setFieldValues(prev => {
            const updated = { ...prev, 'CX.DOC.TYPE': newDocType };

            // compute allowed list per mapping
            const docField = (group?.Fields || []).find(f => f.id === 'CX.DOC.TYPE');
            const mapping = docField?.dependentOptions?.map ?? {};
            const allowed = Array.isArray(mapping[newDocType]) ? mapping[newDocType] : [];

            // if current income not allowed, clear it in state (so UI shows empty)
            if (updated[incomeId] && allowed.length > 0 && !allowed.some(a => (a ?? '').toString().trim().localeCompare(updated[incomeId] ?? '', undefined, { sensitivity: 'accent' }) === 0)) {
                updated[incomeId] = '';
            }
            return updated;
        });
    };

    // Determines whether a given field should be visible in the current group
    const shouldShowField = (field, group) => {
        // Always skip hidden fields
        if (field.hidden) return false;

        // Always show fields in condition-based groups (ShowIf logic)
        if (group.ShowIf) return true;

        // Handle special logic for "Documentation vs Income" group
        if (group.Group === 'Documentation vs Income') {
            const docField = (group.Fields || []).find(f => f.id === 'CX.DOC.TYPE');
            const mapping = docField?.dependentOptions?.map ?? {};

            const docVal = (fieldValues['CX.DOC.TYPE'] ?? '').toString().trim();
            const incVal = (fieldValues['CX.INCOME.TYPE'] ?? '').toString().trim();

            // If either is empty → show both fields
            if (isEmptyField(docVal) || isEmptyField(incVal)) return true;

            // Check allowed values dynamically from JSON mapping
            const allowed = Array.isArray(mapping[docVal])
                ? mapping[docVal].map(v => (v ?? '').toString().trim())
                : [];

            // If mismatch → show both dropdowns to correct
            const docIncomeMismatch =
                allowed.length > 0 &&
                incVal &&
                !allowed.some(
                    a =>
                        a.localeCompare(incVal, undefined, {
                            sensitivity: 'accent',
                        }) === 0
                );

            if (docIncomeMismatch) return true;

            // Otherwise, both valid and filled → hide fields
            return false;
        }

        // Default logic for all other groups: show when field is empty
        const value = fieldValues[field.id];
        return isEmptyField(value);
    };

    // --- licensing page style titles ---
    const msgLower = (licenseStatus.message || '').toLowerCase();

    const isRestrictionMessage =
        msgLower.includes('not eligible for')

    const sectionHeaderText = isRestrictionMessage ? 'Location Restriction' : 'Licensing';

    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText} />

            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={hideToast}
                animationClass={toastAnimation}
            />

            {showZipPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowZipPopup(false)}>
                    <div className="bg-white rounded-lg p-6 w-96 shadow-lg" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Select City & State</h3>
                            <button className="text-gray-500 hover:text-black" onClick={() => setShowZipPopup(false)}>×</button>
                        </div>
                        <ul className="space-y-2">
                            {cityOptions.map((opt, idx) => (
                                <li key={idx}>
                                    <button
                                        onClick={() => {
                                            document.getElementById(`field-${zipSelection.cityId}`).value = opt.city;
                                            document.getElementById(`field-${zipSelection.stateId}`).value = opt.abbrev;
                                            setShowZipPopup(false);
                                        }}
                                        className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                                    >
                                        {opt.city}, {opt.abbrev}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">{pageConfig?.Header}</h2>

                    {!skipModuleValidations && licenseStatus?.blocked ? (
                        <div className="border border-blue-200 rounded bg-gray-50">
                            <div className="section-header">{sectionHeaderText}</div>

                            <div className="p-4 space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">
                                        Blocking
                                    </div>
                                    <div>
                                        <div className="text-base font-semibold">
                                            {isRestrictionMessage ? 'Location restriction' : 'Licensing verification required'}
                                            {isRestrictionMessage && licenseStatus.city
                                                ? ` for ${licenseStatus.city}, ${licenseStatus.stateCode}`
                                                : licenseStatus.stateCode
                                                    ? ` for ${licenseStatus.stateCode} state`
                                                    : ''}
                                        </div>
                                        <p
                                            className="text-base text-red-700 mt-1 font-medium"
                                            dangerouslySetInnerHTML={{
                                                __html: licenseStatus.message || licenseStatus.reason || ''
                                            }}
                                        />

                                    </div>
                                </div>

                                {!isRestrictionMessage && (
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="border rounded p-3 bg-white">
                                            <div className="text-sm font-medium mb-1">Company / Broker License</div>
                                            <div className="text-sm">
                                                {licenseStatus.companyRequired ? (
                                                    licenseStatus.companyHas ? (
                                                        <span className="inline-flex items-center text-green-700">
                                                            <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center text-red-700">
                                                            <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="inline-flex items-center text-gray-600">
                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="border rounded p-3 bg-white">
                                            <div className="text-sm font-medium mb-1">Loan Originator License</div>
                                            <div className="text-sm">
                                                {licenseStatus.loRequired ? (
                                                    licenseStatus.loHas ? (
                                                        <span className="inline-flex items-center text-green-700">
                                                            <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center text-red-700">
                                                            <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="inline-flex items-center text-gray-600">
                                                        <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Show Module Section only if Module is not cleared */}
                            {!allModulesCleared() && (
                                pageConfig?.FieldGroups
                                    .filter(group => group.Type === 'Module')
                                    .filter(group => shouldShowGroup(group))
                                    .map((group, index) => (
                                        <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                            <div className="section-header">{group.Group}</div>
                                            {group.Notes && (
                                                <div
                                                    className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                    dangerouslySetInnerHTML={{ __html: group.Notes }}
                                                />
                                            )}
                                            <div className="grid md:grid-cols-2 gap-6 p-4">
                                                {group.Fields.map(field => (
                                                    <div key={field.id}>
                                                        <input
                                                            type="text"
                                                            className="hidden"
                                                            id={`field-${field.id}`}
                                                            defaultValue={fieldValues[field.id]}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="mt-2 btn em-btn-tertiary"
                                                            onClick={() => redirectToPage(field.pagetoRedirect, loanId)}
                                                        >
                                                            {field.name}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                            )}
                        </>
                    )}

                    {allModulesCleared() && !allFieldsCleared() && (
                        <>
                            <div>
                                <div className="flex justify-between items-center mt-4 mb-2">
                                    <p className="text-sm text-red-600 italic mb-2">All fields are required to {pageName}.</p>
                                </div>
                                {pageConfig?.FieldGroups
                                    .filter(group => shouldShowGroup(group) && group.Type === 'Field')
                                    .map((group, index) => (
                                        <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                            <div className="section-header">{group.Group}</div>

                                            <div className="grid md:grid-cols-2 gap-6 p-4">
                                                {group.Fields
                                                    .filter(field => shouldShowField(field, group))
                                                    .map(field => (
                                                        <div key={field.id}>
                                                            {field.datatype !== 'button' && (
                                                                <label className="form-label mb-1" htmlFor={`field-${field.id}`}>
                                                                    {field.name} <span className="text-red-600">*</span>
                                                                </label>
                                                            )}

                                                            {field.datatype === 'State' ? (
                                                                <select className="form-input w-full text-sm border rounded p-1" id={`field-${field.id}`} defaultValue="">
                                                                    <option value="">Select State</option>
                                                                    {US_STATES.map(state => (
                                                                        <option key={state.abbrev} value={state.abbrev}>
                                                                            {state.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : field.datatype === 'Date' ? (
                                                                <input
                                                                    type="date"
                                                                    className="form-input-medium"
                                                                    id={`field-${field.id}`}
                                                                    defaultValue={fieldValues[field.id] ? new Date(fieldValues[field.id]).toISOString().split('T')[0] : ''}
                                                                />

                                                            ) : field.datatype === 'dropdown' ? (
                                                                (() => {

                                                                    if (field.id === 'CX.DOC.TYPE') {
                                                                        return (
                                                                            <select
                                                                                className="form-input w-full text-sm border rounded p-1"
                                                                                id={`field-${field.id}`}
                                                                                defaultValue={fieldValues[field.id] || ""}
                                                                                onChange={(e) => {
                                                                                    const newVal = e.target.value;

                                                                                    handleDocTypeChange(newVal, group);
                                                                                }}
                                                                            >
                                                                                <option value="">Select an Option</option>
                                                                                {field.options.map(opt => (
                                                                                    <option key={opt} value={opt}>
                                                                                        {opt}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        );
                                                                    }

                                                                    if (field.id === 'CX.INCOME.TYPE') {

                                                                        const docField = (group.Fields || []).find(f => f.id === 'CX.DOC.TYPE');
                                                                        const mapping = docField?.dependentOptions?.map ?? {};
                                                                        const currentDoc = (fieldValues['CX.DOC.TYPE'] ?? '').toString().trim();
                                                                        const allowed = Array.isArray(mapping[currentDoc]) ? mapping[currentDoc] : field.options;

                                                                        return (
                                                                            <select
                                                                                className="form-input w-full text-sm border rounded p-1"
                                                                                id={`field-${field.id}`}
                                                                                defaultValue={fieldValues[field.id] || ""}
                                                                            >
                                                                                <option value="">Select an Option</option>
                                                                                {allowed.map(opt => {

                                                                                    const val = typeof opt === 'string' ? opt : opt.value;
                                                                                    const txt = typeof opt === 'string' ? opt : opt.text;
                                                                                    return (
                                                                                        <option key={val} value={val}>
                                                                                            {txt ?? val}
                                                                                        </option>
                                                                                    );
                                                                                })}
                                                                            </select>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <select
                                                                            className="form-input w-full text-sm border rounded p-1"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id] || ""}
                                                                        >
                                                                            <option value="">Select an Option</option>
                                                                            {/* resolveFieldOptions turns canonical loan-program
                                                                                codes into this org's product names and drops the
                                                                                products it does not carry. */}
                                                                            {resolveFieldOptions(field).map(opt => (
                                                                                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                                                    {opt.text}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    id={`field-${field.id}`}
                                                                    defaultValue={fieldValues[field.id]}
                                                                    onBlur={() => field.datatype === 'Zip' && handleZipBlur(field)}
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    ))}

                                <div className="text-right mt-8">
                                    <button onClick={handleSave} className="btn em-btn-primary">Save</button>
                                </div>
                            </div>
                        </>
                    )}

                </div>
            </div >
        </>
    );
};


const PricingValidate = () => {
    const config = requiredFieldConfigRaw.Pages.find(p => p.Page === 'Product Pricing & Lock');
    return <PricingValidationRenderer pageName="Product Pricing & Lock" config={config} redirectPage="Product Pricing & Lock" />;
};

export default PricingValidate;
